import { useQuery } from "@tanstack/react-query";
import api, { standardListApi } from "@/services/api";
import type {
  ReferenceCompoundItem,
  ReferenceCompoundListResponse,
  ReferenceCompoundParams,
  StandardSearchResponse,
  StandardSpectraResponse,
} from "@/types/referenceCompound";

/**
 * 참조 화합물 목록 조회 (GET /api/v1/reference-compounds)
 *
 * 주의: trailing slash 없이 호출 (307 redirect 회피)
 *
 * `category`/`tier` 는 **백엔드가 지원한다**(Sprint 23-D, 2026-08-20 실측 확인:
 * `GET /api/v1/reference-compounds?category=core` → 서버 필터링). 아래 클라이언트
 * 필터는 구형 백엔드가 파라미터를 무시하고 전체를 돌려주는 경우의 안전망이다.
 *
 * **실패를 삼키지 않는다 (2026-08-20 교차 리뷰 HIGH 2건).** 종전에는 조회가
 * 실패하면 category 조회는 빈 배열, 그 외에는 정적 `COMPOUND_DATABASE` 를
 * 돌려줬다. 그 결과 화면은 서버가 500 인데도 "등록된 화합물이 없습니다"라고
 * 단정하거나, "중앙대 실측 125종" 자리에 정적 목업 28종을 실측인 양 채웠다.
 * 둘 다 없는 사실을 만드는 일이다 — 이제 오류는 오류로 올려보내고, 화면이
 * "불러오지 못했다"고 말한다. (정적 fallback 이 필요한 화면은 스스로 병합한다:
 * `components/ai-models/ReferenceDBExplorer.tsx`.)
 */
export function useReferenceCompounds(params?: ReferenceCompoundParams) {
  return useQuery<ReferenceCompoundItem[]>({
    queryKey: ["reference-compounds", params],
    queryFn: async () => {
      const res = await api.get<ReferenceCompoundListResponse>(
        "/reference-compounds",
        { params },
      );
      let items = res.data.items;
      // 안전망 — 서버가 이미 걸렀으면 이 필터는 아무것도 바꾸지 않는다.
      if (params?.category) {
        const cat = params.category;
        items = items.filter((c) => c.category === cat);
      }
      return items;
    },
    staleTime: 5 * 60 * 1000,
    // placeholderData 를 두지 않는다 — 두면 첫 로딩 중에도 status 가 success 라
    // 화면이 "데이터 없음"을 먼저 보여준다(로딩과 없음은 다른 사실이다).
  });
}

/**
 * 표준 라이브러리(30만+ NIST/Wiley) 개별 화합물 검색 (관리자·비관리자 공용).
 * GET /api/v1/reference-compounds/standard-search (require_viewer)
 *
 * q는 2자 이상일 때만 조회한다 — 백엔드 계약과 동일한 최소 길이 기준.
 * `enabled`로 외부(디바운스 완료 여부 등)에서 추가 제어 가능.
 */
export function useStandardSearch(q: string, enabled = true) {
  const trimmed = q.trim();
  return useQuery<StandardSearchResponse>({
    queryKey: ["reference-compounds", "standard-search", trimmed],
    queryFn: async () => {
      const res = await api.get<StandardSearchResponse>(
        "/reference-compounds/standard-search",
        { params: { q: trimmed, limit: 50 } },
      );
      return res.data;
    },
    enabled: enabled && trimmed.length >= 2,
    staleTime: 60 * 1000,
  });
}

/**
 * 표준 라이브러리 물질의 MS 스펙트럼 (T6-3, 2026-08-27).
 * GET /api/v1/reference-compounds/standard-spectra/{rowid} (require_viewer)
 *
 * `rowid`는 keyset 목록(위치 이동)이 주는 실제 rowid만 유효하다 — 포함 검색
 * 결과는 서버가 rowid를 안 주므로 화면단에서 음수 합성 인덱스를 쓰는데, 그
 * 값으로는 이 엔드포인트를 호출하지 않는다(호출부 책임).
 *
 * 404는 "이 물질은 스펙트럼이 없다"는 **정상 답**이다 — 네트워크 오류처럼
 * 재시도하지 않는다(`retry: false`). 호출부는 `isError`/`error`로 404와 그
 * 외 오류를 구분해 다른 문구를 보여준다.
 */
export function useStandardSpectra(rowid: number | null, enabled: boolean) {
  return useQuery<StandardSpectraResponse>({
    queryKey: ["reference-compounds", "standard-spectra", rowid],
    queryFn: async () => {
      if (rowid == null) {
        throw new Error("rowid is required");
      }
      const res = await standardListApi.spectra(rowid);
      return res.data;
    },
    enabled: enabled && rowid != null,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}
